import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Input,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { awsImageRootUrl } from "lib/constants";
import { useUploadImageMutation } from "lib/features/s3/s3Service";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { v4 as uuid } from "uuid";
import UploadIcon from "../../../assets/icons/upload_icon.svg";
import { getCroppedImage } from "../../../util/cropImage";

interface ImageUploadAndSnapProps {
  onSelectImage: (image: string) => void;
  variant?: "round" | "box"; // round = avatar | box = business (16:9)
  defaultImage?: string | null;
}

const ImageUpload: React.FC<ImageUploadAndSnapProps> = ({
  onSelectImage,
  variant = "round",
  defaultImage,
}) => {
  const [uploadImage, { data: responseData, isLoading }] =
    useUploadImageMutation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [openCropper, setOpenCropper] = useState(false);

  const theme = useTheme();

  // Resize image before uploading to S3
  const resizeAndUploadImage = async (image: string) => {
    const id = uuid() + ".jpg";
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    if (ctx && canvas) {
      const img = new Image();
      img.onload = async () => {
        const targetWidth = 1080;
        const scaleFactor = targetWidth / img.width;
        const targetHeight = img.height * scaleFactor;

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        ctx.clearRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        const snapshot = canvas.toDataURL("image/jpg").split(",")[1];
        const fileSize = atob(snapshot).length;

        const payload = {
          id,
          image: {
            base64: snapshot,
            width: targetWidth,
            height: targetHeight,
            fileSize,
            uri: "",
            type: "image/jpg",
            fileName: `${id}`,
          },
        };
        await uploadImage(payload);
      };
      img.src = image;
    }
  };

  // Upload after cropping
  useEffect(() => {
    if (image) {
      resizeAndUploadImage(image);
    }
  }, [image]);

  // When upload is complete, pass image ID back
  useEffect(() => {
    const _imageId = responseData;
    const _urlFromResponse = `${awsImageRootUrl}/${_imageId}`;

    if (
      responseData &&
      _urlFromResponse &&
      _urlFromResponse !== defaultImage &&
      _imageId
    ) {
      onSelectImage(_imageId);
    }
  }, [responseData]);

  // Handle file selection
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        if (e.target?.result) {
          setImage(e.target.result as string);
          setOpenCropper(true);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Crop complete callback
  const onCropComplete = useCallback((_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Confirm crop and upload
  const handleCropConfirm = async () => {
    try {
      if (!image || !croppedAreaPixels) return;
      const croppedImage = await getCroppedImage(image, croppedAreaPixels);
      setOpenCropper(false);
      setImage(croppedImage);
    } catch (e) {
      console.error("Crop failed", e);
    }
  };

  // Upload button UI
  const renderUploadButton = () => (
    <Button
      variant="contained"
      component="label"
      sx={{
        width: variant === "round" ? "12rem" : "100%",
        height: variant === "round" ? "12rem" : "auto",
        aspectRatio: variant === "box" ? "16 / 9" : "1 / 1",
        borderRadius: variant === "round" ? "50%" : "5px",
        border: variant === "box" ? "2px solid black" : "none",
        color: theme.palette.text.primary,
        backgroundColor:
          variant === "box" ? "transparent" : theme.palette.grey[200],
        "&:hover": {
          backgroundColor:
            variant === "box" ? "transparent" : theme.palette.grey[300],
          boxShadow: "none",
        },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "1rem",
        boxShadow: "none",
      }}
    >
      <img src={UploadIcon} alt="upload-icon" />
      Upload Photo
      <Input
        type="file"
        onChange={handleImageUpload}
        inputProps={{
          accept: "image/*",
          capture: "environment",
        }}
        sx={{ display: "none" }}
      />
    </Button>
  );

  return (
    <>
      {isLoading ? (
        <CircularProgress size={variant === "round" ? 100 : 120} />
      ) : defaultImage ? (
        <Button
          component="label"
          style={{
            position: "relative",
            display: "inline-block",
            cursor: "pointer",
            width: variant === "round" ? "12rem" : "100%",
            aspectRatio: variant === "box" ? "16 / 9" : "1 / 1",
          }}
        >
          <img
            src={defaultImage}
            alt="Uploaded"
            style={{
              width: "100%",
              height: "100%",
              borderRadius: variant === "round" ? "50%" : "5px",
              objectFit: "cover",
            }}
          />
          <Input
            type="file"
            onChange={handleImageUpload}
            inputProps={{
              accept: "image/*",
              capture: "environment",
            }}
            sx={{ display: "none" }}
          />
        </Button>
      ) : (
        renderUploadButton()
      )}

      <canvas
        ref={canvasRef}
        width={500}
        height={500}
        style={{ display: "none" }}
      ></canvas>

      <Dialog
        open={openCropper}
        onClose={() => setOpenCropper(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent style={{ position: "relative", height: 400 }}>
          <Cropper
            image={image || ""}
            crop={crop}
            zoom={zoom}
            aspect={variant === "box" ? 16 / 9 : 1}  
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            cropShape={variant === "box" ? "rect" : "round"} 
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCropper(false)}>Cancel</Button>
          <Button onClick={handleCropConfirm} variant="contained">
            Crop & Upload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImageUpload;
